/**
 * CR-5454: the Dashboard > Cost tab's own filters.
 *
 * - "Next 20 weeks" is the Cost tab's default time period (the original
 *   platform's Cost page opened on it) and a Quick Date Range on that tab only.
 *   Every other tab looks backwards, so it is neither offered nor kept there.
 * - Contract Status / Part Number / Contract Type narrow the Cost figures and
 *   are shown on the Cost tab only.
 *
 * The backend applies all of them through one rule
 * (`purchasing.cost_line_filters`) on both the Cost cards and the drill-downs
 * they open. These helpers build the matching query params for each side.
 *
 * Framework-free and unit-tested (see __tests__/costTabFilters.test.ts).
 */
import type { DashboardFilterState } from './types';
import { formatLocalDate, getLast12MonthsRange, getNext20WeeksRange } from './dashboardFilterUtils';

export { getNext20WeeksRange };

export const NEXT_20_WEEKS = 'next20Weeks';

/** CR-1421 decision 2 (Aaron, 15 Oct 2026): legacy's other two Cost periods. */
export const ALL_FUTURE_WEEKS = 'allFutureWeeks';
export const MAX_NEXT_WEEKS = 100;

/**
 * Legacy's "Next N weeks": this Monday through the Sunday N weeks on (this week
 * plus the next N, as Next 20 weeks is).
 *
 * @param n - whole weeks, 1 to 100
 * @param now - reference date
 * @returns the range as local YYYY-MM-DD strings
 */
export function getNextWeeksRange(n: number, now: Date = new Date()): { dateFrom: string; dateTo: string } {
  const { dateFrom } = getNext20WeeksRange(now);
  const [y, m, d] = dateFrom.split('-').map(Number);
  return { dateFrom, dateTo: formatLocalDate(new Date(y, m - 1, d + 7 * (n + 1) - 1)) };
}

/** Legacy's "All future weeks": from this Monday, with no end. */
export function getAllFutureWeeksRange(now: Date = new Date()): { dateFrom: string; dateTo: string } {
  return { dateFrom: getNext20WeeksRange(now).dateFrom, dateTo: '' };
}

/**
 * Which forward Cost period the dates are, if any.
 *
 * @returns ``{ kind: 'all' }``, ``{ kind: 'next', weeks }`` (1-100), or null
 */
export function forwardCostPeriod(
  filters: Pick<DashboardFilterState, 'dateFrom' | 'dateTo'>, now: Date = new Date(),
): { kind: 'all' } | { kind: 'next'; weeks: number } | null {
  const monday = getNext20WeeksRange(now).dateFrom;
  if (filters.dateFrom !== monday) return null;
  if (!filters.dateTo) return { kind: 'all' };
  for (let n = 1; n <= MAX_NEXT_WEEKS; n += 1) {
    if (getNextWeeksRange(n, now).dateTo === filters.dateTo) return { kind: 'next', weeks: n };
  }
  return null;
}

/** Contract Status choices, as sent to the API. */
export const CONTRACT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Both' },
  { value: 'contracted', label: 'Contracted' },
  { value: 'not_contracted', label: 'Not Contracted' },
];

type CostFilterKeys = 'contractStatus' | 'partNumber' | 'contractType';

/** Whether the filter dates are exactly the Next 20 Weeks range. */
export function isNext20WeeksActive(filters: Pick<DashboardFilterState, 'dateFrom' | 'dateTo'>, now: Date = new Date()): boolean {
  const r = getNext20WeeksRange(now);
  return filters.dateFrom === r.dateFrom && filters.dateTo === r.dateTo;
}

/**
 * Filters to apply on leaving the Cost tab: the Cost-only filters are cleared
 * and a Next 20 weeks range falls back to the page default, because no other
 * tab offers either.
 *
 * @returns the new state, or null when nothing needs to change
 */
export function leaveCostTab(filters: DashboardFilterState, now: Date = new Date()): DashboardFilterState | null {
  const hasCostFilters = Boolean(filters.contractStatus || filters.partNumber || filters.contractType);
  // CR-1421 decision 2: any forward Cost period (Next 20 / Next N / All future
  // weeks) falls back to the page default, since no other tab offers one.
  const next20 = forwardCostPeriod(filters, now) !== null;
  if (!hasCostFilters && !next20) return null;
  const out: DashboardFilterState = { ...filters, contractStatus: '', partNumber: '', contractType: '' };
  return next20 ? { ...out, ...getLast12MonthsRange() } : out;
}

/**
 * Contract Status / Part Number / Contract Type as API params.
 *
 * @param filters - current dashboard filter state
 * @returns the params that are set (empty values omitted)
 */
export function costLineParams(filters: Partial<Pick<DashboardFilterState, CostFilterKeys>> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!filters) return out;
  if (filters.contractStatus) out['contract_status'] = filters.contractStatus;
  const part = (filters.partNumber || '').trim();
  if (part) out['part_number_contains'] = part;
  if (filters.contractType) out['contract_type'] = filters.contractType;
  return out;
}

/**
 * The incoming window — the Cost tab's time period applied to what is still to
 * come in. Arrears are overdue as of today, so their requests never carry it.
 *
 * @param filters - current dashboard filter state
 * @returns `incoming_from` / `incoming_to` for the set dates
 */
export function incomingWindowParams(filters: Pick<DashboardFilterState, 'dateFrom' | 'dateTo'> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters?.dateFrom) out['incoming_from'] = filters.dateFrom;
  if (filters?.dateTo) out['incoming_to'] = filters.dateTo;
  return out;
}

/**
 * Everything the Cost tab adds to the dashboard_stats request.
 *
 * @param filters - current dashboard filter state
 * @returns line filters plus the incoming window
 */
export function costTabRequestParams(filters: DashboardFilterState): Record<string, string> {
  return { ...costLineParams(filters), ...incomingWindowParams(filters) };
}
